import {
    Alert,
    Box,
    Button,
    Chip,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import { PlayArrow, TrackChanges } from '@mui/icons-material'

import {
    useGetIntentStatusQuery,
    useListIntentExportsQuery,
    useListIntentSegmentsQuery,
    useRunIntentExportMutation,
    type IntentExport,
} from '@/apis/intentApi'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { useProjectId } from '@/hooks/useReportArgs'
import { Kpi, ReportHeader, ReportStatus, SectionCard, fmtNum } from '@/modules/reports/common'

import {
    CONNECTOR_LABEL,
    categoriesLabel,
    connectorNotesShown,
    memberLabel,
    runSummary,
    runTone,
    stagesLabel,
} from './intentModel'

/**
 * YOM Intent (docs/intent.md): in-market segments built from this project's own consented
 * visitors, and the audiences sent to the project's own ad and email accounts. Segments and
 * exports are set up through the API; this page shows them, their counts and export status.
 */
export function IntentPage() {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const subTenant = useAppSelector(s => s.project.subTenant)
    const status = useGetIntentStatusQuery(projectId)
    const enabled = status.data?.enabled === true
    const segments = useListIntentSegmentsQuery(projectId, { skip: !enabled })
    const exports = useListIntentExportsQuery(projectId, {
        skip: !enabled,
        pollingInterval: 30_000,
        skipPollingIfUnfocused: true,
    })
    const [runExport, runState] = useRunIntentExportMutation()

    // The sub-tenant picker narrows the page to one member; unset shows every member.
    const inScope = (st: string) => subTenant === null || st === subTenant
    const memberRows = (status.data?.members ?? []).filter(m => inScope(m.sub_tenant))
    const profiles = memberRows.reduce((sum, m) => sum + m.profiles, 0)
    const segmentRows = (segments.data ?? []).filter(s => inScope(s.sub_tenant))
    const exportRows = (exports.data ?? []).filter(e => inScope(e.sub_tenant))
    const segmentName = new Map((segments.data ?? []).map(s => [s.id, s.name]))
    const exportable = segmentRows.reduce((sum, s) => sum + (s.count?.advertising ?? 0), 0)
    const notes = connectorNotesShown(status.data?.connector_notes, exportRows)

    const run = async (e: IntentExport) => {
        try {
            await runExport({ projectId, id: e.id }).unwrap()
            dispatch(
                showNotification({
                    message: `Sending "${e.name}". The status below updates when it finishes.`,
                    severity: 'success',
                }),
            )
        } catch (err) {
            dispatch(
                showNotification({
                    message: v2ErrorMessage(err) ?? 'Could not start the export',
                    severity: 'error',
                }),
            )
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Intent"
                subtitle="Who is shopping for what on your own site, and the audiences you send to your own ad accounts."
            />
            <ReportStatus isLoading={status.isLoading} error={status.error}>
                {!enabled ? (
                    <Alert severity="info">
                        {status.data?.deployment === false
                            ? 'Intent is not available on this deployment yet.'
                            : 'Intent is off for this project. It can be turned on once the customer has accepted the YOM Intent Addendum (yom.aapastech.com/intent-addendum): set intent.addendumAcceptedAt to the acceptance time and intent.enabled to true in the project config.'}
                    </Alert>
                ) : (
                    <>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Kpi
                                    title="Profiles"
                                    value={fmtNum(profiles)}
                                    hint="Visitors with analytics consent who showed interest in a product category"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Kpi title="Segments" value={fmtNum(segmentRows.length)} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Kpi
                                    title="Exportable people in segments"
                                    value={fmtNum(exportable)}
                                    hint="Advertising consent seen in the last 180 days. Only these people are ever sent to an ad or email account."
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mb: 3 }}>
                            <SectionCard title="Segments">
                                <ReportStatus
                                    isLoading={segments.isLoading}
                                    error={segments.error}
                                    isEmpty={segmentRows.length === 0}
                                    emptyIcon={<TrackChanges />}
                                    emptyTitle="No segments yet"
                                    emptyDescription="Create one with POST /v2/projects/:id/intent/segments (docs/intent.md §7)."
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Member</TableCell>
                                                <TableCell>Categories</TableCell>
                                                <TableCell>Stages</TableCell>
                                                <TableCell align="right">People</TableCell>
                                                <TableCell align="right">Exportable</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {segmentRows.map(s => (
                                                <TableRow key={s.id}>
                                                    <TableCell>{s.name}</TableCell>
                                                    <TableCell>
                                                        {memberLabel(s.sub_tenant)}
                                                    </TableCell>
                                                    <TableCell>{categoriesLabel(s)}</TableCell>
                                                    <TableCell>{stagesLabel(s)}</TableCell>
                                                    <TableCell align="right">
                                                        {fmtNum(s.count?.profiles ?? 0)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {fmtNum(s.count?.advertising ?? 0)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </ReportStatus>
                            </SectionCard>
                        </Box>

                        <SectionCard title="Exports">
                            <ReportStatus
                                isLoading={exports.isLoading}
                                error={exports.error}
                                isEmpty={exportRows.length === 0}
                                emptyTitle="No exports yet"
                                emptyDescription={`Connectors on this deployment: ${
                                    status.data?.connectors.length
                                        ? status.data.connectors.join(', ')
                                        : 'none'
                                }.`}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Destination</TableCell>
                                            <TableCell>Segment</TableCell>
                                            <TableCell>Schedule</TableCell>
                                            <TableCell>Last run</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {exportRows.map(e => (
                                            <TableRow key={e.id}>
                                                <TableCell>{e.name}</TableCell>
                                                <TableCell>
                                                    {CONNECTOR_LABEL[e.connector]}
                                                    {e.explicit_only && (
                                                        <Tooltip title="Only people who gave advertising consent themselves, not by a regional default">
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label="Explicit consent only"
                                                                sx={{ ml: 1 }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {segmentName.get(e.segment_id) ?? e.segment_id}
                                                </TableCell>
                                                <TableCell>
                                                    {e.schedule === 'daily' ? 'Daily' : 'On demand'}
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Chip
                                                            size="small"
                                                            color={runTone(e.last_run)}
                                                            label={e.last_run?.status ?? 'never'}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {runSummary(e.last_run)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Send the segment now (admins)">
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                startIcon={<PlayArrow />}
                                                                disabled={
                                                                    !e.active || runState.isLoading
                                                                }
                                                                onClick={() => {
                                                                    run(e).catch(() => undefined)
                                                                }}
                                                            >
                                                                Run
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ReportStatus>
                            {notes.map(n => (
                                <Alert key={n.connector} severity="info" sx={{ mt: 2 }}>
                                    <strong>{CONNECTOR_LABEL[n.connector]}:</strong> {n.note}
                                </Alert>
                            ))}
                        </SectionCard>
                    </>
                )}
            </ReportStatus>
        </Box>
    )
}
